<template>
  <div>
    <FlightDescriptionHeader class="mb-3" :segment="segment" />
    <div class="relative space-y-3">
      <div
        class="bg-background-neutral-tertiary absolute top-0 left-[15px] h-full w-0.5"
      ></div>
      <FlightPoint
        is-departure
        :source-page="sourcePage"
        :flight-idx="flightIdx"
        :segment-idx="segmentIdx"
        :point="segment.dep"
        :dependent-point="prevSegmentArr"
      />
      <StopLocation
        v-for="(location, idx) in stopLocations"
        :key="idx"
        :location="location"
      />
      <FlightPoint
        :source-page="sourcePage"
        :flight-idx="flightIdx"
        :segment-idx="segmentIdx"
        :point="segment.arr"
        :dependent-point="nextSegmentDep"
      />
    </div>
    <NewHandLuggageInfo
      v-if="newHandLuggageValue"
      :new-hand-luggage-value="newHandLuggageValue"
      @onNewHandLuggageClick="$emit('onNewHandLuggageClick')"
    />
    <BaggagesInfo
      v-if="displayLuggage && (isBaggageVisible || isHandLuggageVisible)"
      class="mt-4"
      :baggage="baggage"
      :hand-luggage="handLuggage"
      :new-hand-luggage-value="newHandLuggageValue"
      :is-baggage-visible="isBaggageVisible"
      :is-hand-luggage-visible="isHandLuggageVisible"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { isObject } from '@aviata/type-guards';
  import { ticketDetails } from 'shared';
  import { isNull } from 'lodash-es';

  import FlightDescriptionHeader from './FlightDescriptionHeader.vue';
  import FlightPoint from './FlightPoint.vue';
  import BaggagesInfo from './BaggagesInfo.vue';
  import StopLocation from './StopLocation.vue';
  import NewHandLuggageInfo from './NewHandLuggageInfo.vue';

  const props = defineProps<{
    sourcePage?: string;
    segmentIdx?: number;
    flightIdx?: number;
    segment: ticketDetails.types.MappedSegment;
    prevSegmentArr: ticketDetails.types.MappedSegmentPoint | null;
    nextSegmentDep: ticketDetails.types.MappedSegmentPoint | null;
    displayLuggage: boolean;
  }>();

  defineEmits<{
    onNewHandLuggageClick: [];
  }>();

  // TODO: временное решение для FS ручной клади, удалить позже
  const newHandLuggageValue = computed(() =>
    ticketDetails.lib.getConvertedLuggageValue(props.segment.handLuggage),
  );

  const isRailSegment = computed(() =>
    ticketDetails.lib.isRailSegment(props.segment),
  );

  const stopLocations = computed(() =>
    isRailSegment.value ? [] : props.segment.stops.locations,
  );

  /**
   * Computes whether baggage should be visible.
   *
   * Logic:
   * 1. If `baggage` contains a `unit` property, it belongs to a accounts service, and visibility is always `true`.
   * 2. Otherwise, check `baggage.free`:
   *    - If `free` is `null` or `undefined`, visibility is `false`.
   *    - If `free` is an object, visibility is `true`.
   *    - If `free.ADT` is null and `free.YTH` is null, visibility is `false`.
   *
   * @link https://git.aviata.team/self-service/accounts
   * @returns {boolean} `true` if baggage should be visible, otherwise `false`.
   */
  const isBaggageVisible = computed(() => {
    const { baggage } = props.segment;

    // If `baggage` has a `unit` property, it belongs to the accounts service
    if (ticketDetails.lib.isLuggageFromAccounts(baggage)) {
      return baggage.value > 0;
    }

    // If `baggage.free` is not an object, return false
    if (!baggage.free || !isObject(baggage.free)) {
      return false;
    }

    // If `baggage.free.ADT` and `baggage.free.YTH` is not null, return false
    if (isNull(baggage.free.ADT) && isNull(baggage.free.YTH)) {
      return false;
    }

    return true;
  });

  /**
   * Computes whether hand luggage should be visible.
   *
   * Logic:
   * 1. If `handLuggage` contains a `unit` property, it belongs to a accounts service, and visibility is always `true`.
   * 2. Otherwise, check `handLuggage.free`:
   *    - If `free` is `null` or `undefined`, visibility is `false`.
   *    - If `free` is an object, visibility is `true`.
   *    - If `free.ADT` is null and `free.YTH` is null, visibility is `false`.
   *
   * @link https://git.aviata.team/self-service/accounts
   * @returns {boolean} `true` if handLuggage should be visible, otherwise `false`.
   */
  const isHandLuggageVisible = computed(() => {
    const { handLuggage } = props.segment;

    if (newHandLuggageValue.value) {
      return false;
    }

    // If `handLuggage` has a `unit` property, it belongs to the accounts service
    if (ticketDetails.lib.isLuggageFromAccounts(handLuggage)) {
      return handLuggage.value > 0;
    }

    // If `handLuggage.free` is not an object, return false
    if (!handLuggage.free || !isObject(handLuggage.free)) {
      return false;
    }

    // If `handLuggage.free.ADT` and `handLuggage.free.YTH` is not null, return false
    if (isNull(handLuggage.free.ADT) && isNull(handLuggage.free.YTH)) {
      return false;
    }

    return true;
  });

  const baggage = computed(() => {
    const { baggage } = props.segment;

    return ticketDetails.lib.isLuggageFromAccounts(baggage)
      ? ticketDetails.lib.luggageToStringFromAccounts(baggage)
      : ticketDetails.lib.luggageToString({ luggage: baggage });
  });

  const handLuggage = computed(() => {
    const { handLuggage } = props.segment;

    return ticketDetails.lib.isLuggageFromAccounts(handLuggage)
      ? ticketDetails.lib.luggageToStringFromAccounts(handLuggage)
      : ticketDetails.lib.luggageToString({ luggage: handLuggage });
  });
</script>
